import { IconType, LazyIconProps } from '@photosi/rn-components/lazy-icon/LazyIcon.types';
import { lazyImport } from 'src/_shared/utils/lazy-import/lazyImport';
import { Icon } from 'src/theme-v2/icon';

export type IconProps = {
  name: IconName;
  size?: number;
  accessibilityLabel?: string;
  accessible?: boolean;
} & Pick<LazyIconProps, 'filled' | 'ref' | 'testID' | 'type'>;

export const IconColorByType = new Map<IconType, string>([
  [IconType.PRIMARY, Icon.IconColorPrimary],
  [IconType.SECONDARY, Icon.IconColorSecondary],
  [IconType.TERTIARY, Icon.IconColorTertiary],
  [IconType.PLACEHOLDER, Icon.IconColorPlaceholder],
  [IconType.INTERACTIVE, Icon.IconColorInteractive],
  [IconType.BRAND, Icon.IconColorBrand],
  [IconType.PROMO, Icon.IconColorPromo],
  [IconType.DELIVERY, Icon.IconColorDelivery],
  [IconType.SUCCESS, Icon.IconColorSuccess],
  [IconType.ALERT, Icon.IconColorAlert],
  [IconType.ERROR, Icon.IconColorError],
  [IconType.DISABLED, Icon.IconColorDisabled],
  [IconType.ON_DARK, Icon.IconColorOnDark],
  [IconType.INVERTED, Icon.IconColorInverted],
]);

export enum ICON_NAMES {
  ArrowLeft1 = 'arrow-left-1',
  ArrowRight1 = 'arrow-right-1',
  ArrowBottom1 = 'arrow-bottom-1',
  ArrowUp1 = 'arrow-up-1',
  ArrowLeft2 = 'arrow-left-2',
  ArrowRight2 = 'arrow-right-2',
  ArrowBottom2 = 'arrow-bottom-2',
  ArrowUp2 = 'arrow-up-2',
  ArrowRightUp = 'arrow-right-up',
  Transfer = 'transfer',
  Redo = 'redo',
  Undo = 'undo',
  Trend = 'trend',
  Check = 'check',
  CheckCircle = 'check-circle',
  Close = 'close',
  CloseCircle = 'close-circle',
  Plus = 'plus',
  Minus = 'minus',
  Drag = 'drag',
  Vertical = 'vertical',
  More = 'more',
  Menu = 'menu',
  Location = 'location',
  Location2 = 'location-2',
  Navigation = 'navigation',
  Share = 'share',
  Share2 = 'share-2',
  Link = 'link',
  ZoomPlus = 'zoom-plus',
  ZoomMinus = 'zoom-minus',
  Search = 'search',
  SearchAI = 'search-ai',
  FAQ = 'faq',
  Bell = 'bell',
  BellRing = 'bell-ring',
  Smartphone = 'smartphone',
  Smartphone2 = 'smartphone-2',
  RotateSmartphone = 'rotate-smartphone',
  Tablet = 'tablet',
  Desktop = 'desktop',
  Pc = 'pc',
  Phone = 'phone',
  Settings = 'settings',
  Settings2 = 'settings-2',
  Clock = 'clock',
  Mail = 'mail',
  Printer = 'printer',
  Offline = 'offline',
  Person = 'person',
  People = 'people',
  People2 = 'people-2',
  Smile = 'smile',
  Error = 'error',
  Like = 'like',
  Dislike = 'dislike',
  Eye = 'eye',
  EyeBarred = 'eye-barred',
  Lock = 'lock',
  Ban = 'ban',
  Logout = 'logout',
  SimpleChat = 'simple-chat',
  Chat = 'chat',
  Assistance = 'assistance',
  Help = 'help',
  Question = 'question',
  Info = 'info',
  Info2 = 'info-2',
  DiscountSimple = 'discount-simple',
  DiscountSimple2 = 'discount-simple-2',
  MoneyBox = 'money-box',
  Cash = 'cash',
  Gift = 'gift',
  Star = 'star',
  Sun = 'sun',
  Moon = 'moon',
  Lamp = 'lamp',
  Diamond = 'diamond',
  Rocket = 'rocket',
  EcoSustainable = 'eco-sustainable',
  Delete = 'delete',
  Palette = 'palette',
  Edit = 'edit',
  Note = 'note',
  Request = 'request',
  SUbscription = 'subscription',
  Folder = 'folder',
  Bag = 'bag',
  Copy = 'copy',
  Home = 'home',
  PencilRuler = 'pencil-ruler',
  PlusCircle = 'plus-circle',
  Delivery = 'delivery',
  Express = 'express',
  Retailer = 'retailer',
  Cart = 'cart',
  AddToCart = 'add-to-cart',
  Discount = 'discount',
  AI = 'ai',
  ChatAI = 'chat-ai',
  ChatAI2 = 'chat-ai-2',
  PhotoSiStar = 'photosi-star',
  GiftCard = 'gift-card',
  Puzzle = 'puzzle',
  Vintage = 'vintage',
  Poster = 'poster',
  HappyCase = 'happy-case',
  Gallery = 'gallery',
  Box = 'box',
  AllItems = 'all-items',
  Birthday = 'birthday',
  Love = 'love',
  DoubleHeart = 'double-heart',
  LoveDesignCollection = 'love-design-collection',
  BabyCarriage = 'baby-carriage',
  Moustache = 'moustache',
  Suitcase = 'suitcase',
  Chalice = 'chalice',
  Church = 'church',
  Travel = 'travel',
  ChirstmasBauble = 'christmas-bauble',
  Backpack = 'backpack',
  Flower = 'flower',
  ChristmasTree = 'christmas-tree',
  TravelDesignCollection = 'travel-design-collection',
  KidsCollection = 'kids-collection',
  Filters = 'filters',
  Filters2 = 'filters-2',
  Contrast = 'contrast',
  Drop = 'drop',
  Drop2 = 'drop-2',
  Brush = 'brush',
  Sticker = 'sticker',
  ChangeSticker = 'change-sticker',
  Magic = 'magic',
  Glass = 'glass',
  PasspartoutColor = 'passpartout-color',
  NoCut = 'no-cut',
  Cut = 'cut',
  Layers = 'layers',
  BrushDimension = 'brush-dimension',
  BorderChange = 'border-change',
  NoFocus = 'no-focus',
  Background = 'background',
  Text = 'text',
  Text2 = 'text-2',
  TextAlignCenter = 'text-align-center',
  Saved = 'saved',
  Cloud = 'cloud',
  SaveFailed = 'save-failed',
  Upload = 'upload',
  Warning = 'warning',
  FrameTint = 'frame-tint',
  PhotoPaper = 'photo-paper',
  SideColor = 'side-color',
  Edge = 'edge',
  Thickness = 'thickness',
  Opacity = 'opacity',
  ChangeImage = 'change-image',
  Picker = 'picker',
  AutoColorCorrection = 'auto-color-correction',
  Settings3 = 'settings-3',
  FlipHorizontal = 'flip-horizontal',
  FlipVertical = 'flip-vertical',
  Flip = 'flip',
  TextAlignLeft = 'text-align-left',
  TextAlignRight = 'text-align-right',
  Ruler = 'ruler',
  Ruler2 = 'ruler-2',
  RulerOff = 'ruler-off',
  SelectAll = 'select-all',
  SelectAll2 = 'select-all-2',
  Multiselect2 = 'multiselect-2',
  Collapse = 'collapse',
  Expand = 'expand',
  Counterclockwise = 'counterclockwise',
  Crop = 'crop',
  Refresh = 'refresh',
  Crop2 = 'crop-2',
  Copy3 = 'copy-3',
  CopyText = 'copy-text',
  Hammer = 'hammer',
  Material = 'material',
  Target = 'target',
  Write = 'write',
  Multiselect3 = 'multiselect-3',
  Square = 'square',
  Border = 'border',
  AddFrame = 'add-frame',
  Frame = 'frame',
  Space = 'space',
  Layout = 'layout',
  Format = 'format',
  TypeOfPaper = 'type-of-paper',
  PhotoAlbum = 'photo-album',
  PicturePro = 'picture-pro',
  AddPhoto = 'add-photo',
  Picture = 'picture',
  UploadFailed = 'upload-failed',
  EditingPhoto = 'editing-photo',
  FloppyDiskSave = 'floppy-disk-save',
  FloppyDiskSaved = 'floppy-disk-saved',
  RotateRight = 'rotate-right',
  RotateLeft = 'rotate-left',
  Rotate = 'rotate',
  Unknow = 'unknow',
  Play = 'play',
  Stop = 'stop',
  Send = 'send',
  CameraFlash = 'camera-flash',
  Camera = 'camera',
  Focus = 'focus',
  Multiselect = 'multiselect',
  GridView4 = 'grid-view-4',
  Circle = 'circle',
  Calendar = 'calendar',
  RemoveDates = 'remove-dates',
  Maps = 'maps',
  RemoveMaps = 'remove-maps',
  Orientation = 'orientation',
  Orientation2 = 'orientation-2',
  ChangeType = 'change-type',
  Book = 'book',
  AddPage = 'add-page',
  Warning2 = 'warning-2',
  Pause = 'pause',
  Tap = 'tap',
  TextBackground = 'text-background',
  TextDesign = 'text-design',
  Rearrange = 'rearrange',
  GridView1 = 'grid-view-1',
  GridView2 = 'grid-view-2',
  GridView25 = 'grid-view-2.5',
  GridView3 = 'grid-view-3',
  GooglePhoto = 'google-photo',
  Facebook = 'facebook',
  Google = 'google',
  Apple = 'apple',
  AmazonPhotos = 'amazon-photos',
  IOSPhoto = 'ios-photo',
}

export const iconLibrary = {
  [ICON_NAMES.ArrowLeft1]: lazyImport(() => import('./library/ArrowLeft1'), 'ArrowLeft1'),
  [ICON_NAMES.ArrowRight1]: lazyImport(() => import('./library/ArrowRight1'), 'ArrowRight1'),
  [ICON_NAMES.ArrowBottom1]: lazyImport(() => import('./library/ArrowBottom1'), 'ArrowBottom1'),
  [ICON_NAMES.ArrowUp1]: lazyImport(() => import('./library/ArrowUp1'), 'ArrowUp1'),
  [ICON_NAMES.ArrowLeft2]: lazyImport(() => import('./library/ArrowLeft2'), 'ArrowLeft2'),
  [ICON_NAMES.ArrowRight2]: lazyImport(() => import('./library/ArrowRight2'), 'ArrowRight2'),
  [ICON_NAMES.ArrowBottom2]: lazyImport(() => import('./library/ArrowBottom2'), 'ArrowBottom2'),
  [ICON_NAMES.ArrowUp2]: lazyImport(() => import('./library/ArrowUp2'), 'ArrowUp2'),
  [ICON_NAMES.ArrowRightUp]: lazyImport(() => import('./library/ArrowRightUp'), 'ArrowRightUp'),
  [ICON_NAMES.Transfer]: lazyImport(() => import('./library/Transfer'), 'Transfer'),
  [ICON_NAMES.Redo]: lazyImport(() => import('./library/Redo'), 'Redo'),
  [ICON_NAMES.Undo]: lazyImport(() => import('./library/Undo'), 'Undo'),
  [ICON_NAMES.Trend]: lazyImport(() => import('./library/Trend'), 'Trend'),
  [ICON_NAMES.Check]: lazyImport(() => import('./library/Check'), 'Check'),
  [ICON_NAMES.CheckCircle]: lazyImport(() => import('./library/CheckCircle'), 'CheckCircle'),
  [ICON_NAMES.Close]: lazyImport(() => import('./library/Close'), 'Close'),
  [ICON_NAMES.CloseCircle]: lazyImport(() => import('./library/CloseCircle'), 'CloseCircle'),
  [ICON_NAMES.Plus]: lazyImport(() => import('./library/Plus'), 'Plus'),
  [ICON_NAMES.Minus]: lazyImport(() => import('./library/Minus'), 'Minus'),
  [ICON_NAMES.Drag]: lazyImport(() => import('./library/Drag'), 'Drag'),
  [ICON_NAMES.Vertical]: lazyImport(() => import('./library/Vertical'), 'Vertical'),
  [ICON_NAMES.More]: lazyImport(() => import('./library/More'), 'More'),
  [ICON_NAMES.Menu]: lazyImport(() => import('./library/Menu'), 'Menu'),
  [ICON_NAMES.Location]: lazyImport(() => import('./library/Location'), 'Location'),
  [ICON_NAMES.Location2]: lazyImport(() => import('./library/Location2'), 'Location2'),
  [ICON_NAMES.Navigation]: lazyImport(() => import('./library/Navigation'), 'Navigation'),
  [ICON_NAMES.Share]: lazyImport(() => import('./library/Share'), 'Share'),
  [ICON_NAMES.Share2]: lazyImport(() => import('./library/Share2'), 'Share2'),
  [ICON_NAMES.Link]: lazyImport(() => import('./library/Link'), 'Link'),
  [ICON_NAMES.ZoomPlus]: lazyImport(() => import('./library/ZoomPlus'), 'ZoomPlus'),
  [ICON_NAMES.ZoomMinus]: lazyImport(() => import('./library/ZoomMinus'), 'ZoomMinus'),
  [ICON_NAMES.Search]: lazyImport(() => import('./library/Search'), 'Search'),
  [ICON_NAMES.SearchAI]: lazyImport(() => import('./library/SearchAI'), 'SearchAI'),
  [ICON_NAMES.FAQ]: lazyImport(() => import('./library/FAQ'), 'FAQ'),
  [ICON_NAMES.Bell]: lazyImport(() => import('./library/Bell'), 'Bell'),
  [ICON_NAMES.BellRing]: lazyImport(() => import('./library/BellRing'), 'BellRing'),
  [ICON_NAMES.Smartphone]: lazyImport(() => import('./library/Smartphone'), 'Smartphone'),
  [ICON_NAMES.Smartphone2]: lazyImport(() => import('./library/Smartphone2'), 'Smartphone2'),
  [ICON_NAMES.RotateSmartphone]: lazyImport(() => import('./library/RotateSmartphone'), 'RotateSmartphone'),
  [ICON_NAMES.Tablet]: lazyImport(() => import('./library/Tablet'), 'Tablet'),
  [ICON_NAMES.Desktop]: lazyImport(() => import('./library/Desktop'), 'Desktop'),
  [ICON_NAMES.Pc]: lazyImport(() => import('./library/Pc'), 'Pc'),
  [ICON_NAMES.Phone]: lazyImport(() => import('./library/Phone'), 'Phone'),
  [ICON_NAMES.Settings]: lazyImport(() => import('./library/Settings'), 'Settings'),
  [ICON_NAMES.Settings2]: lazyImport(() => import('./library/Settings2'), 'Settings2'),
  [ICON_NAMES.Clock]: lazyImport(() => import('./library/Clock'), 'Clock'),
  [ICON_NAMES.Mail]: lazyImport(() => import('./library/Mail'), 'Mail'),
  [ICON_NAMES.Printer]: lazyImport(() => import('./library/Printer'), 'Printer'),
  [ICON_NAMES.Offline]: lazyImport(() => import('./library/Offline'), 'Offline'),
  [ICON_NAMES.Person]: lazyImport(() => import('./library/Person'), 'Person'),
  [ICON_NAMES.People]: lazyImport(() => import('./library/People'), 'People'),
  [ICON_NAMES.People2]: lazyImport(() => import('./library/People2'), 'People2'),
  [ICON_NAMES.Smile]: lazyImport(() => import('./library/Smile'), 'Smile'),
  [ICON_NAMES.Error]: lazyImport(() => import('./library/Error'), 'Error'),
  [ICON_NAMES.Like]: lazyImport(() => import('./library/Like'), 'Like'),
  [ICON_NAMES.Dislike]: lazyImport(() => import('./library/Dislike'), 'Dislike'),
  [ICON_NAMES.Eye]: lazyImport(() => import('./library/Eye'), 'Eye'),
  [ICON_NAMES.EyeBarred]: lazyImport(() => import('./library/EyeBarred'), 'EyeBarred'),
  [ICON_NAMES.Lock]: lazyImport(() => import('./library/Lock'), 'Lock'),
  [ICON_NAMES.Ban]: lazyImport(() => import('./library/Ban'), 'Ban'),
  [ICON_NAMES.Logout]: lazyImport(() => import('./library/Logout'), 'Logout'),
  [ICON_NAMES.SimpleChat]: lazyImport(() => import('./library/SimpleChat'), 'SimpleChat'),
  [ICON_NAMES.Chat]: lazyImport(() => import('./library/Chat'), 'Chat'),
  [ICON_NAMES.Assistance]: lazyImport(() => import('./library/Assistance'), 'Assistance'),
  [ICON_NAMES.Help]: lazyImport(() => import('./library/Help'), 'Help'),
  [ICON_NAMES.Question]: lazyImport(() => import('./library/Question'), 'Question'),
  [ICON_NAMES.Info]: lazyImport(() => import('./library/Info'), 'Info'),
  [ICON_NAMES.Info2]: lazyImport(() => import('./library/Info2'), 'Info2'),
  [ICON_NAMES.DiscountSimple]: lazyImport(() => import('./library/DiscountSimple'), 'DiscountSimple'),
  [ICON_NAMES.DiscountSimple2]: lazyImport(() => import('./library/DiscountSimple2'), 'DiscountSimple2'),
  [ICON_NAMES.MoneyBox]: lazyImport(() => import('./library/MoneyBox'), 'MoneyBox'),
  [ICON_NAMES.Cash]: lazyImport(() => import('./library/Cash'), 'Cash'),
  [ICON_NAMES.Gift]: lazyImport(() => import('./library/Gift'), 'Gift'),
  [ICON_NAMES.Star]: lazyImport(() => import('./library/Star'), 'Star'),
  [ICON_NAMES.Sun]: lazyImport(() => import('./library/Sun'), 'Sun'),
  [ICON_NAMES.Moon]: lazyImport(() => import('./library/Moon'), 'Moon'),
  [ICON_NAMES.Lamp]: lazyImport(() => import('./library/Lamp'), 'Lamp'),
  [ICON_NAMES.Diamond]: lazyImport(() => import('./library/Diamond'), 'Diamond'),
  [ICON_NAMES.Rocket]: lazyImport(() => import('./library/Rocket'), 'Rocket'),
  [ICON_NAMES.EcoSustainable]: lazyImport(() => import('./library/EcoSustainable'), 'EcoSustainable'),
  [ICON_NAMES.Delete]: lazyImport(() => import('./library/Delete'), 'Delete'),
  [ICON_NAMES.Palette]: lazyImport(() => import('./library/Palette'), 'Palette'),
  [ICON_NAMES.Edit]: lazyImport(() => import('./library/Edit'), 'Edit'),
  [ICON_NAMES.Note]: lazyImport(() => import('./library/Note'), 'Note'),
  [ICON_NAMES.Request]: lazyImport(() => import('./library/Request'), 'Request'),
  [ICON_NAMES.SUbscription]: lazyImport(() => import('./library/Subscription'), 'Subscription'),
  [ICON_NAMES.Folder]: lazyImport(() => import('./library/Folder'), 'Folder'),
  [ICON_NAMES.Bag]: lazyImport(() => import('./library/Bag'), 'Bag'),
  [ICON_NAMES.Copy]: lazyImport(() => import('./library/Copy'), 'Copy'),
  [ICON_NAMES.Home]: lazyImport(() => import('./library/Home'), 'Home'),
  [ICON_NAMES.PencilRuler]: lazyImport(() => import('./library/PencilRuler'), 'PencilRuler'),
  [ICON_NAMES.PlusCircle]: lazyImport(() => import('./library/PlusCircle'), 'PlusCircle'),
  [ICON_NAMES.Delivery]: lazyImport(() => import('./library/Delivery'), 'Delivery'),
  [ICON_NAMES.Express]: lazyImport(() => import('./library/Express'), 'Express'),
  [ICON_NAMES.Retailer]: lazyImport(() => import('./library/Retailer'), 'Retailer'),
  [ICON_NAMES.Cart]: lazyImport(() => import('./library/Cart'), 'Cart'),
  [ICON_NAMES.AddToCart]: lazyImport(() => import('./library/AddToCart'), 'AddToCart'),
  [ICON_NAMES.Discount]: lazyImport(() => import('./library/Discount'), 'Discount'),
  [ICON_NAMES.AI]: lazyImport(() => import('./library/AI'), 'AI'),
  [ICON_NAMES.ChatAI]: lazyImport(() => import('./library/ChatAI'), 'ChatAI'),
  [ICON_NAMES.ChatAI2]: lazyImport(() => import('./library/ChatAI2'), 'ChatAI2'),
  [ICON_NAMES.PhotoSiStar]: lazyImport(() => import('./library/PhotoSiStar'), 'PhotoSiStar'),
  [ICON_NAMES.GiftCard]: lazyImport(() => import('./library/GiftCard'), 'GiftCard'),
  [ICON_NAMES.Puzzle]: lazyImport(() => import('./library/Puzzle'), 'Puzzle'),
  [ICON_NAMES.Vintage]: lazyImport(() => import('./library/Vintage'), 'Vintage'),
  [ICON_NAMES.Poster]: lazyImport(() => import('./library/Poster'), 'Poster'),
  [ICON_NAMES.HappyCase]: lazyImport(() => import('./library/HappyCase'), 'HappyCase'),
  [ICON_NAMES.Gallery]: lazyImport(() => import('./library/Gallery'), 'Gallery'),
  [ICON_NAMES.Box]: lazyImport(() => import('./library/Box'), 'Box'),
  [ICON_NAMES.AllItems]: lazyImport(() => import('./library/AllItems'), 'AllItems'),
  [ICON_NAMES.Birthday]: lazyImport(() => import('./library/Birthday'), 'Birthday'),
  [ICON_NAMES.Love]: lazyImport(() => import('./library/Love'), 'Love'),
  [ICON_NAMES.DoubleHeart]: lazyImport(() => import('./library/DoubleHeart'), 'DoubleHeart'),
  [ICON_NAMES.LoveDesignCollection]: lazyImport(() => import('./library/LoveDesignCollection'), 'LoveDesignCollection'),
  [ICON_NAMES.BabyCarriage]: lazyImport(() => import('./library/BabyCarriage'), 'BabyCarriage'),
  [ICON_NAMES.Moustache]: lazyImport(() => import('./library/Moustache'), 'Moustache'),
  [ICON_NAMES.Suitcase]: lazyImport(() => import('./library/Suitcase'), 'Suitcase'),
  [ICON_NAMES.Chalice]: lazyImport(() => import('./library/Chalice'), 'Chalice'),
  [ICON_NAMES.Church]: lazyImport(() => import('./library/Church'), 'Church'),
  [ICON_NAMES.Travel]: lazyImport(() => import('./library/Travel'), 'Travel'),
  [ICON_NAMES.ChirstmasBauble]: lazyImport(() => import('./library/ChristmasBauble'), 'ChirstmasBauble'),
  [ICON_NAMES.Backpack]: lazyImport(() => import('./library/Backpack'), 'Backpack'),
  [ICON_NAMES.Flower]: lazyImport(() => import('./library/Flower'), 'Flower'),
  [ICON_NAMES.ChristmasTree]: lazyImport(() => import('./library/ChristmasTree'), 'ChristmasTree'),
  [ICON_NAMES.TravelDesignCollection]: lazyImport(
    () => import('./library/TravelDesignCollection'),
    'TravelDesignCollection',
  ),
  [ICON_NAMES.KidsCollection]: lazyImport(() => import('./library/KidsCollection'), 'KidsCollection'),
  [ICON_NAMES.Filters]: lazyImport(() => import('./library/Filters'), 'Filters'),
  [ICON_NAMES.Filters2]: lazyImport(() => import('./library/Filters2'), 'Filters2'),
  [ICON_NAMES.Contrast]: lazyImport(() => import('./library/Contrast'), 'Contrast'),
  [ICON_NAMES.Drop]: lazyImport(() => import('./library/Drop'), 'Drop'),
  [ICON_NAMES.Drop2]: lazyImport(() => import('./library/Drop2'), 'Drop2'),
  [ICON_NAMES.Brush]: lazyImport(() => import('./library/Brush'), 'Brush'),
  [ICON_NAMES.Sticker]: lazyImport(() => import('./library/Sticker'), 'Sticker'),
  [ICON_NAMES.ChangeSticker]: lazyImport(() => import('./library/ChangeSticker'), 'ChangeSticker'),
  [ICON_NAMES.Magic]: lazyImport(() => import('./library/Magic'), 'Magic'),
  [ICON_NAMES.Glass]: lazyImport(() => import('./library/Glass'), 'Glass'),
  [ICON_NAMES.PasspartoutColor]: lazyImport(() => import('./library/PasspartoutColor'), 'PasspartoutColor'),
  [ICON_NAMES.NoCut]: lazyImport(() => import('./library/NoCut'), 'NoCut'),
  [ICON_NAMES.Cut]: lazyImport(() => import('./library/Cut'), 'Cut'),
  [ICON_NAMES.Layers]: lazyImport(() => import('./library/Layers'), 'Layers'),
  [ICON_NAMES.BrushDimension]: lazyImport(() => import('./library/BrushDimension'), 'BrushDimension'),
  [ICON_NAMES.BorderChange]: lazyImport(() => import('./library/BorderChange'), 'BorderChange'),
  [ICON_NAMES.NoFocus]: lazyImport(() => import('./library/NoFocus'), 'NoFocus'),
  [ICON_NAMES.Background]: lazyImport(() => import('./library/Background'), 'Background'),
  [ICON_NAMES.Text]: lazyImport(() => import('./library/Text'), 'Text'),
  [ICON_NAMES.Text2]: lazyImport(() => import('./library/Text2'), 'Text2'),
  [ICON_NAMES.TextAlignCenter]: lazyImport(() => import('./library/TextAlignCenter'), 'TextAlignCenter'),
  [ICON_NAMES.Saved]: lazyImport(() => import('./library/Saved'), 'Saved'),
  [ICON_NAMES.Cloud]: lazyImport(() => import('./library/Cloud'), 'Cloud'),
  [ICON_NAMES.SaveFailed]: lazyImport(() => import('./library/SaveFailed'), 'SaveFailed'),
  [ICON_NAMES.Upload]: lazyImport(() => import('./library/Upload'), 'Upload'),
  [ICON_NAMES.Warning]: lazyImport(() => import('./library/Warning'), 'Warning'),
  [ICON_NAMES.FrameTint]: lazyImport(() => import('./library/FrameTint'), 'FrameTint'),
  [ICON_NAMES.PhotoPaper]: lazyImport(() => import('./library/PhotoPaper'), 'PhotoPaper'),
  [ICON_NAMES.SideColor]: lazyImport(() => import('./library/SideColor'), 'SideColor'),
  [ICON_NAMES.Edge]: lazyImport(() => import('./library/Edge'), 'Edge'),
  [ICON_NAMES.Thickness]: lazyImport(() => import('./library/Thickness'), 'Thickness'),
  [ICON_NAMES.Opacity]: lazyImport(() => import('./library/Opacity'), 'Opacity'),
  [ICON_NAMES.ChangeImage]: lazyImport(() => import('./library/ChangeImage'), 'ChangeImage'),
  [ICON_NAMES.Picker]: lazyImport(() => import('./library/Picker'), 'Picker'),
  [ICON_NAMES.AutoColorCorrection]: lazyImport(() => import('./library/AutoColorCorrection'), 'AutoColorCorrection'),
  [ICON_NAMES.Settings3]: lazyImport(() => import('./library/Settings3'), 'Settings3'),
  [ICON_NAMES.FlipHorizontal]: lazyImport(() => import('./library/FlipHorizontal'), 'FlipHorizontal'),
  [ICON_NAMES.FlipVertical]: lazyImport(() => import('./library/FlipVertical'), 'FlipVertical'),
  [ICON_NAMES.Flip]: lazyImport(() => import('./library/Flip'), 'Flip'),
  [ICON_NAMES.TextAlignLeft]: lazyImport(() => import('./library/TextAlignLeft'), 'TextAlignLeft'),
  [ICON_NAMES.TextAlignRight]: lazyImport(() => import('./library/TextAlignRight'), 'TextAlignRight'),
  [ICON_NAMES.Ruler]: lazyImport(() => import('./library/Ruler'), 'Ruler'),
  [ICON_NAMES.Ruler2]: lazyImport(() => import('./library/Ruler2'), 'Ruler2'),
  [ICON_NAMES.RulerOff]: lazyImport(() => import('./library/RulerOff'), 'RulerOff'),
  [ICON_NAMES.SelectAll]: lazyImport(() => import('./library/SelectAll'), 'SelectAll'),
  [ICON_NAMES.SelectAll2]: lazyImport(() => import('./library/SelectAll2'), 'SelectAll2'),
  [ICON_NAMES.Multiselect2]: lazyImport(() => import('./library/Multiselect2'), 'Multiselect2'),
  [ICON_NAMES.Collapse]: lazyImport(() => import('./library/Collapse'), 'Collapse'),
  [ICON_NAMES.Expand]: lazyImport(() => import('./library/Expand'), 'Expand'),
  [ICON_NAMES.Counterclockwise]: lazyImport(() => import('./library/Counterclockwise'), 'Counterclockwise'),
  [ICON_NAMES.Crop]: lazyImport(() => import('./library/Crop'), 'Crop'),
  [ICON_NAMES.Refresh]: lazyImport(() => import('./library/Refresh'), 'Refresh'),
  [ICON_NAMES.Crop2]: lazyImport(() => import('./library/Crop2'), 'Crop2'),
  [ICON_NAMES.Copy3]: lazyImport(() => import('./library/Copy3'), 'Copy3'),
  [ICON_NAMES.CopyText]: lazyImport(() => import('./library/CopyText'), 'CopyText'),
  [ICON_NAMES.Hammer]: lazyImport(() => import('./library/Hammer'), 'Hammer'),
  [ICON_NAMES.Material]: lazyImport(() => import('./library/Material'), 'Material'),
  [ICON_NAMES.Target]: lazyImport(() => import('./library/Target'), 'Target'),
  [ICON_NAMES.Write]: lazyImport(() => import('./library/Write'), 'Write'),
  [ICON_NAMES.Multiselect3]: lazyImport(() => import('./library/Multiselect3'), 'Multiselect3'),
  [ICON_NAMES.Square]: lazyImport(() => import('./library/Square'), 'Square'),
  [ICON_NAMES.Border]: lazyImport(() => import('./library/Border'), 'Border'),
  [ICON_NAMES.AddFrame]: lazyImport(() => import('./library/AddFrame'), 'AddFrame'),
  [ICON_NAMES.Frame]: lazyImport(() => import('./library/Frame'), 'Frame'),
  [ICON_NAMES.Space]: lazyImport(() => import('./library/Space'), 'Space'),
  [ICON_NAMES.Layout]: lazyImport(() => import('./library/Layout'), 'Layout'),
  [ICON_NAMES.Format]: lazyImport(() => import('./library/Format'), 'Format'),
  [ICON_NAMES.TypeOfPaper]: lazyImport(() => import('./library/TypeOfPaper'), 'TypeOfPaper'),
  [ICON_NAMES.PhotoAlbum]: lazyImport(() => import('./library/PhotoAlbum'), 'PhotoAlbum'),
  [ICON_NAMES.PicturePro]: lazyImport(() => import('./library/PicturePro'), 'PicturePro'),
  [ICON_NAMES.AddPhoto]: lazyImport(() => import('./library/AddPhoto'), 'AddPhoto'),
  [ICON_NAMES.Picture]: lazyImport(() => import('./library/Picture'), 'Picture'),
  [ICON_NAMES.UploadFailed]: lazyImport(() => import('./library/UploadFailed'), 'UploadFailed'),
  [ICON_NAMES.EditingPhoto]: lazyImport(() => import('./library/EditingPhoto'), 'EditingPhoto'),
  [ICON_NAMES.FloppyDiskSave]: lazyImport(() => import('./library/FloppyDiskSave'), 'FloppyDiskSave'),
  [ICON_NAMES.FloppyDiskSaved]: lazyImport(() => import('./library/FloppyDiskSaved'), 'FloppyDiskSaved'),
  [ICON_NAMES.RotateRight]: lazyImport(() => import('./library/RotateRight'), 'RotateRight'),
  [ICON_NAMES.RotateLeft]: lazyImport(() => import('./library/RotateLeft'), 'RotateLeft'),
  [ICON_NAMES.Rotate]: lazyImport(() => import('./library/Rotate'), 'Rotate'),
  [ICON_NAMES.Unknow]: lazyImport(() => import('./library/Unknow'), 'Unknow'),
  [ICON_NAMES.Play]: lazyImport(() => import('./library/Play'), 'Play'),
  [ICON_NAMES.Stop]: lazyImport(() => import('./library/Stop'), 'Stop'),
  [ICON_NAMES.Send]: lazyImport(() => import('./library/Send'), 'Send'),
  [ICON_NAMES.CameraFlash]: lazyImport(() => import('./library/CameraFlash'), 'CameraFlash'),
  [ICON_NAMES.Camera]: lazyImport(() => import('./library/Camera'), 'Camera'),
  [ICON_NAMES.Focus]: lazyImport(() => import('./library/Focus'), 'Focus'),
  [ICON_NAMES.Multiselect]: lazyImport(() => import('./library/Multiselect'), 'Multiselect'),
  [ICON_NAMES.GridView4]: lazyImport(() => import('./library/GridView4'), 'GridView4'),
  [ICON_NAMES.Circle]: lazyImport(() => import('./library/Circle'), 'Circle'),
  [ICON_NAMES.Calendar]: lazyImport(() => import('./library/Calendar'), 'Calendar'),
  [ICON_NAMES.RemoveDates]: lazyImport(() => import('./library/RemoveDates'), 'RemoveDates'),
  [ICON_NAMES.Maps]: lazyImport(() => import('./library/Maps'), 'Maps'),
  [ICON_NAMES.RemoveMaps]: lazyImport(() => import('./library/RemoveMaps'), 'RemoveMaps'),
  [ICON_NAMES.Orientation]: lazyImport(() => import('./library/Orientation'), 'Orientation'),
  [ICON_NAMES.Orientation2]: lazyImport(() => import('./library/Orientation2'), 'Orientation2'),
  [ICON_NAMES.ChangeType]: lazyImport(() => import('./library/ChangeType'), 'ChangeType'),
  [ICON_NAMES.Book]: lazyImport(() => import('./library/Book'), 'Book'),
  [ICON_NAMES.AddPage]: lazyImport(() => import('./library/AddPage'), 'AddPage'),
  [ICON_NAMES.Warning2]: lazyImport(() => import('./library/Warning2'), 'Warning2'),
  [ICON_NAMES.Pause]: lazyImport(() => import('./library/Pause'), 'Pause'),
  [ICON_NAMES.Tap]: lazyImport(() => import('./library/Tap'), 'Tap'),
  [ICON_NAMES.TextBackground]: lazyImport(() => import('./library/TextBackground'), 'TextBackground'),
  [ICON_NAMES.TextDesign]: lazyImport(() => import('./library/TextDesign'), 'TextDesign'),
  [ICON_NAMES.Rearrange]: lazyImport(() => import('./library/Rearrange'), 'Rearrange'),
  [ICON_NAMES.GridView1]: lazyImport(() => import('./library/GridView1'), 'GridView1'),
  [ICON_NAMES.GridView2]: lazyImport(() => import('./library/GridView2'), 'GridView2'),
  [ICON_NAMES.GridView25]: lazyImport(() => import('./library/GridView2-5'), 'GridView25'),
  [ICON_NAMES.GridView3]: lazyImport(() => import('./library/GridView3'), 'GridView3'),
  [ICON_NAMES.GooglePhoto]: lazyImport(() => import('./library/GooglePhoto'), 'GooglePhoto'),
  [ICON_NAMES.Facebook]: lazyImport(() => import('./library/Facebook'), 'Facebook'),
  [ICON_NAMES.Google]: lazyImport(() => import('./library/Google'), 'Google'),
  [ICON_NAMES.Apple]: lazyImport(() => import('./library/Apple'), 'Apple'),
  [ICON_NAMES.AmazonPhotos]: lazyImport(() => import('./library/AmazonPhotos'), 'AmazonPhotos'),
  [ICON_NAMES.IOSPhoto]: lazyImport(() => import('./library/IOSPhoto'), 'IOSPhoto'),
};

export type IconName = keyof typeof iconLibrary;
